import PropTypes from 'prop-types';
import React, { Fragment, useMemo } from 'react';
import { isEmpty } from 'validate.js';

import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import Divider from 'dpl/components/BreederProfile/mobile/components/shared/Divider';
import SmartImage from 'dpl/components/SmartImage';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';

import {
  getPuppyTransportOptions,
  TRANSPORTER_DESCRIPTION
} from './utils/constants';

const IS_LT_MD = isLessThanBreakpoint('md');

/** @param {any} props */
export default function PuppyTransportOptionsModal({
  isOpen,
  onClose,
  transportOptions = null,
  breeder
}) {
  const modalHeader = 'Puppy transport options';

  const dropOffMethods = useMemo(() => {
    const dropOffOptions = getPuppyTransportOptions({
      breederFirstName: breeder.first_name
    });

    if (isEmpty(transportOptions)) return [];

    const methods = dropOffOptions.reduce((acc, method) => {
      const dm = transportOptions.find(opt => opt.key === method.key);

      if (!dm) return acc;

      acc.push({
        ...method,
        transporterDescription: TRANSPORTER_DESCRIPTION.find(
          t => t.value === dm.transporter_description
        )?.title,
        breederCostDescription: dm.breeder_cost_description
      });

      return acc;
    }, []);

    return methods;
  }, [transportOptions, breeder]);

  function ModalContent() {
    return (
      <ul className="flex flex-column gap-24">
        {dropOffMethods.map(
          (
            {
              key,
              title,
              longDescription,
              transporterDescription,
              costDescription,
              breederCostDescription
            },
            index,
            arr
          ) => (
            <Fragment key={key}>
              <li className="flex flex-column gap-16">
                <h4 className="font-sans font-16 md:font-18 lh-title">
                  {title}
                </h4>
                <p className="font-16 secondary">{longDescription}</p>
                <div className="font-14 flex flex-column gap-16 sm:gap-8 md:gap-8 lg:gap-8">
                  {transporterDescription && (
                    <p className="flex flex-column flex-md-row gap-8">
                      <span className="fw-medium">Who will transport</span>
                      <span className="secondary">
                        {transporterDescription}
                      </span>
                    </p>
                  )}
                  <p className="flex flex-column flex-md-row gap-8">
                    <span className="fw-medium">Estimated cost</span>
                    <span className="secondary">{costDescription}</span>
                  </p>
                </div>
                {breederCostDescription && (
                  <div className="bg-sand br12 secondary font-16 ph4 pv4 ph6-md pv6-md">
                    <p className="mb3 mb4-md">{breederCostDescription}</p>
                    <div className="flex items-center gap-8">
                      {breeder.avatar_profile_photo_url ? (
                        <SmartImage
                          Tag="div"
                          src={breeder.avatar_profile_photo_url}
                          className="bg-center cover h-100 w-100 br-100"
                          width="32px"
                          height="32px"
                        />
                      ) : (
                        <UserAvatar
                          shape="round"
                          size="sm"
                          user={{
                            id: breeder.user_id,
                            initials: breeder.initials
                          }}
                        />
                      )}
                      {breeder.full_name}
                    </div>
                  </div>
                )}
              </li>
              {index < arr.length - 1 && (
                <Divider containerClassName="" className="mv0" />
              )}
            </Fragment>
          )
        )}
      </ul>
    );
  }

  if (!IS_LT_MD && !isOpen) {
    return null;
  }

  if (IS_LT_MD) {
    return (
      <FixedTooltipPortal
        className="PuppyTransportOptionsModal"
        isVisible={isOpen}
        style={{ maxHeight: '85vh' }}
        onCloseClick={onClose}
      >
        <div className="ph1 pt3">
          <h2 className="font-20 lh-title mb6">{modalHeader}</h2>
          {!isEmpty(dropOffMethods) && <ModalContent />}
        </div>
      </FixedTooltipPortal>
    );
  }

  return (
    <MiniOverlayModal
      className="PuppyTransportOptionsModal"
      closeButtonClassName="pv2 ph2 mt4 mr4 ba b--light-gray bg-white br-100"
      contentClassName="bg-white"
      closeButtonSize="20px"
      maxWidth="600px"
      onCloseClick={onClose}
      scrollableContent={dropOffMethods.length > 2}
    >
      <div className="flex flex-column h-100 overflow-hidden">
        <h2 className="font-20 md:font-24 lh-title mb6 mt8 ph5 ph8-md">
          {modalHeader}
        </h2>
        <div className="overflow-y-auto ph5 ph8-md pb8">
          {!isEmpty(dropOffMethods) && <ModalContent />}
        </div>
      </div>
    </MiniOverlayModal>
  );
}

PuppyTransportOptionsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  breeder: PropTypes.shape({
    avatar_profile_photo_url: PropTypes.string,
    first_name: PropTypes.string,
    full_name: PropTypes.string,
    user_id: PropTypes.number,
    initials: PropTypes.string
  }).isRequired,
  transportOptions: PropTypes.arrayOf(
    PropTypes.shape({
      breeder_cost_description: PropTypes.string,
      key: PropTypes.string.isRequired,
      transporter_description: PropTypes.string
    })
  )
};
